import {
  GetAllResponse,
  CreateTodoInput,
  UpdateTodoInput,
  CreateFeedbackInput,
  UpdateFeedbackInput,
  GetFeedbackLogResponse,
  GetMyFeedbackResponse,
  UpdateReleaseInput,
  GetReleasesResponse,
  GetReleaseNotesResponse,
  CreateMessageInput,
  GetMessagesResponse,
  CreateInviteResponse,
  AcceptInviteResponse,
  GetSharesResponse,
  GetGrantsResponse,
  SharedProjectResponse,
  TodoData,
  Context,
} from "../../../types";

import { logPile } from "../logPile";

const API_URL = import.meta.env.VITE_API_URL;
const AUTH_URL = import.meta.env.VITE_AUTH_URL;

// doridium's sign-up page on the same stage the auth calls below hit, so a
// dev-stage user creates the account that dev todoit can actually log in with.
export const SIGN_UP_URL = `${AUTH_URL}/user/auth/create`;

export const getToken = () =>
  window?.localStorage?.getItem?.("auth") ?? undefined;
export const setToken = (token: string) =>
  window.localStorage.setItem("auth", token);
export const clearToken = () => window.localStorage.removeItem("auth");

// Every API call leaves a breadcrumb (method, path, status) so a capture's
// timeline shows what the app was doing. Bodies are deliberately not recorded.
// You can do that safely on the recieving side as needed
const authFetch = async (url: string, props?: RequestInit) => {
  const token = getToken();
  const method = props?.method ?? "GET";
  const path = url.replace(API_URL, "");
  try {
    const res = await fetch(url, {
      ...props,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { authorization: token } : {}),
        ...props?.headers,
      },
    });
    logPile.addBreadcrumb({
      category: "http",
      level: res.ok ? "info" : "warning",
      message: `${method} ${path} → ${res.status}`,
      data: { status: res.status },
    });
    // A 5xx gets a capture of its own (one group per route); 4xx are user-level
    // (expired token, not found, conflict) and stay breadcrumbs — the banner the
    // user sees is enough.
    if (res.status >= 500) {
      logPile.captureMessage(`${method} ${path} → ${res.status}`, "warning", {
        tags: { path, status: String(res.status) },
        fingerprint: `api-5xx:${method} ${path}`,
      });
    }
    return res;
  } catch (e) {
    logPile.addBreadcrumb({
      category: "http",
      level: "error",
      message: `${method} ${path} failed`,
    });
    // The API itself unreachable — different from a 5xx, and worth its own capture.
    logPile.captureException(e, { message: `${method} ${path} unreachable`, tags: { path } });
    throw e;
  }
};

// doridium's auth is a separate origin, hit directly — its /auth/* routes
// are already CORS-open. See ROADMAP.md for routing this same-origin later.
// Both return the body plus the HTTP `status`, so callers can tell an expired
// token (401, routine) from doridium being down (5xx, worth a capture).
export const AuthApi = {
  login: async (input: { username: string; password: string }) => {
    const res = await fetch(`${AUTH_URL}/auth/login`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    const body = await res.json().catch(() => ({}));
    return { ...body, status: res.status };
  },
  getUser: async (token: string) => {
    const res = await fetch(`${AUTH_URL}/auth/user`, {
      method: "GET",
      headers: { authorization: token },
    });
    const body = await res.json().catch(() => ({}));
    return { ...body, status: res.status };
  },
};

export const TodoApi = {
  getAll: async (): Promise<{
    success: boolean;
    data?: GetAllResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/todo`);
    return res.json();
  },

  createTodo: async (input: CreateTodoInput) => {
    const res = await authFetch(`${API_URL}/api/todo`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  updateTodo: async (input: UpdateTodoInput) => {
    const res = await authFetch(`${API_URL}/api/todo/${input.id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  deleteTodo: async (id: string, createdAt: string) => {
    const url = new URL(`${API_URL}/api/todo/${id}`);
    url.searchParams.set("createdAt", createdAt);
    const res = await authFetch(url.toString(), { method: "DELETE" });
    // Callers need the HTTP status: 404 means "already deleted", which an
    // optimistic delete treats as success.
    const body = await res.json().catch(() => ({}));
    return { ...body, status: res.status } as {
      success?: boolean;
      status: number;
    };
  },
};

export const FeedbackApi = {
  create: async (input: CreateFeedbackInput) => {
    const res = await authFetch(`${API_URL}/api/feedback`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  update: async (input: UpdateFeedbackInput) => {
    const res = await authFetch(`${API_URL}/api/feedback/${input.id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  delete: async (id: string, createdAt: string, userId: string) => {
    const url = new URL(`${API_URL}/api/feedback/${id}`);
    url.searchParams.set("createdAt", createdAt);
    url.searchParams.set("userId", userId);
    const res = await authFetch(url.toString(), { method: "DELETE" });
    return res.json();
  },

  // The signed-in user's own feedback with a coarse status.
  getMine: async (): Promise<{
    success: boolean;
    data?: GetMyFeedbackResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/my-feedback`);
    return res.json();
  },

  // Admin-only — quietly returns a non-success response for anyone else,
  // which the UI treats as "hide the feedback log" rather than an error.
  getLog: async (): Promise<{
    success: boolean;
    data?: GetFeedbackLogResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/feedback`);
    return res.json();
  },
};

// Release manager — admin-only; a non-admin gets a non-success response,
// which the panel treats as "stay hidden".
export const ReleaseApi = {
  getAll: async (): Promise<{
    success: boolean;
    data?: GetReleasesResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/release`);
    return res.json();
  },

  update: async (input: UpdateReleaseInput) => {
    const res = await authFetch(`${API_URL}/api/release/${input.id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
    return res.json();
  },
};

// Public notes of tagged releases, for any signed-in user.
export const ReleaseNotesApi = {
  get: async (): Promise<{
    success: boolean;
    data?: GetReleaseNotesResponse;
    error?: string;
  }> => {
    const res = await authFetch(`${API_URL}/api/release-notes`);
    return res.json();
  },
};

export const ManagerApi = {
  getThread: async (
    threadId: string,
    after?: string,
  ): Promise<{
    success: boolean;
    data?: GetMessagesResponse;
    error?: string;
  }> => {
    const url = new URL(`${API_URL}/api/message`, window.location.origin);
    url.searchParams.set("threadId", threadId);
    if (after) url.searchParams.set("after", after);
    const res = await authFetch(url.toString());
    return res.json();
  },

  sendMessage: async (input: CreateMessageInput) => {
    const res = await authFetch(`${API_URL}/api/message`, {
      method: "POST",
      body: JSON.stringify(input),
    });
    return res.json();
  },

  markSeen: async (threadId: string) => {
    const res = await authFetch(`${API_URL}/api/thread/${threadId}`, {
      method: "PATCH",
      body: JSON.stringify({
        id: threadId,
        lastSeenBySamAt: new Date().toISOString(),
      }),
    });
    return res.json();
  },
};

// List sharing. Each call throws an Error carrying the HTTP status as `status`
// on failure, so callers can tell 404 / 400 apart without parsing messages.
// The invite token is never put into an error message.
export class SharingError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const sharingCall = async <T>(url: string, props?: RequestInit): Promise<T> => {
  const res = await authFetch(url, props);
  const body = await res.json().catch(() => undefined);
  if (!res.ok || !body?.success) {
    throw new SharingError(
      body?.statusCode ?? res.status,
      body?.error || "Request failed",
    );
  }
  return body.data as T;
};

export const SharingApi = {
  createInvite: (parentId: string, parentCreatedAt: string) =>
    sharingCall<CreateInviteResponse>(`${API_URL}/api/invite`, {
      method: "POST",
      body: JSON.stringify({ type: "invite", parentId, parentCreatedAt }),
    }),

  acceptInvite: (token: string) =>
    sharingCall<AcceptInviteResponse>(
      `${API_URL}/api/invite/${encodeURIComponent(token)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ type: "invite", id: token }),
      },
    ),

  listShares: () => sharingCall<GetSharesResponse>(`${API_URL}/api/shared`),

  getShared: (parentId: string) =>
    sharingCall<SharedProjectResponse>(
      `${API_URL}/api/shared?parentId=${encodeURIComponent(parentId)}`,
    ),

  // Sharee writes: only a new step, or a step's title/done. The server finds
  // the owner from the caller's own share; the body never names one.
  addStep: (input: {
    id: string;
    title: string;
    context: Context;
    projectId: string;
    projectCreatedAt: string;
  }) =>
    sharingCall<TodoData>(`${API_URL}/api/todo`, {
      method: "POST",
      body: JSON.stringify({
        type: "todo",
        id: input.id,
        title: input.title,
        context: input.context,
        parentId: input.projectId,
        sharedProjectId: input.projectId,
        sharedProjectCreatedAt: input.projectCreatedAt,
      }),
    }),

  updateStep: (input: {
    id: string;
    createdAt: string;
    projectId: string;
    updates: { title?: string; done?: boolean };
  }) =>
    sharingCall<TodoData>(
      `${API_URL}/api/todo/${encodeURIComponent(input.id)}`,
      {
        method: "PATCH",
        body: JSON.stringify({
          type: "todo",
          id: input.id,
          createdAt: input.createdAt,
          sharedProjectId: input.projectId,
          ...input.updates,
        }),
      },
    ),

  listGrants: (parentId: string) =>
    sharingCall<GetGrantsResponse>(
      `${API_URL}/api/grant?parentId=${encodeURIComponent(parentId)}`,
    ),

  removeShare: (parentId: string, inviteeId: string) =>
    sharingCall<unknown>(
      `${API_URL}/api/share/${encodeURIComponent(`${parentId}_${inviteeId}`)}`,
      { method: "DELETE" },
    ),
};
